import {type ExtendedHttpIncomingMessage, HttpServerRequest} from './exchange.js';
import {MapHttpHeaders} from '../http/exchange.js';
import type {AuthorizationRule, Principal} from '../../types/auth';
import type {WebSocketHandshakeInfo} from '../../types/web/socket';
import type {Middleware} from './types.js';
import type {ServerWebExchange, ServerWebSocketHandler} from '../../types/web/server';
import {upgradeMatcher} from './util/matchers.ts';
import {ProcessedOriginFilters} from './ws-client-verify.js';
import {HttpStatus} from '../http/status.js';
import type {AsyncLocalStorage} from 'node:async_hooks';

export function createHandshakeInfo(req: ExtendedHttpIncomingMessage, protocol?: string): WebSocketHandshakeInfo {
    const exchange = req?.exchange;
    const request = exchange?.request ?? new HttpServerRequest(req);
    const principalPromiseProvider = exchange?.principal;
    const principal = principalPromiseProvider ? principalPromiseProvider.bind(exchange) : async function principal<P extends Principal> (): Promise<P | undefined> { return undefined; };
    const url = request.URL;
    const headers = new MapHttpHeaders();
    for (const key of request.headers.keys()) {
        headers.set(key, request.headers.list(key));
    }
    const cookies = request.cookies;
    const logPrefix = exchange?.logPrefix ?? `[${request.id}] `;
    const remoteAddress = request.remoteAddress;
    const handshake: WebSocketHandshakeInfo = {
        url,
        headers,
        cookies,
        principal,
        protocol,
        remoteAddress,
        logPrefix,
    };
    return handshake;
}

export function webSockets(context: { sockets: Map<string, SocketRoute> }): Middleware {
    // websocket upgrade handler
    const sockets = async (exchange: ServerWebExchange, next: () => Promise<void>) => {
        const request = exchange.request;
        const path = request.path ?? '/';

        const routes = context.sockets;
        const route = (routes.get(path) ?? Array.from(routes.values()).find(route => {
            if (path === '/' && route.default === true) {
                return true;
            }
        }));

        if (route !== undefined) {
            const {request, response} = exchange;
            const upgradeMatchResult = await upgradeMatcher(exchange);
            if ((request.method === 'GET' || request.method === 'CONNECT') && upgradeMatchResult.match) {
                if (route.upgradeStrategy !== undefined) {
                    route.upgradeStrategy(exchange);
                    return;
                }
                else {
                    throw new Error(`No upgrade strategy defined for route on ${path}`);
                }

            } else {
                if (route.default) {
                    // allow default route to pass through
                    await next();
                    return;
                }

                response.setStatusCode(HttpStatus.UPGRADE_REQUIRED);
                response.headers
                    .set('Upgrade', 'websocket')
                    .set('Connection', 'Upgrade')
                    .set('Content-Type', 'text/plain');

                const buffer = Buffer.from(`This service [${request.path}] requires use of the websocket protocol.`, 'utf-8');
                await response.body(buffer);
            }
        } else {
            await next();
        }
    };
    return [sockets];
}

export type SocketRoute = {
    readonly default?: boolean,
    readonly ping?: number,
    readonly maxConnections?: number
    readonly authorize?: AuthorizationRule,
    readonly originFilters?: ProcessedOriginFilters
    readonly factory: (server: { endpoint: string, storage?: AsyncLocalStorage<{ exchange?, securityContext? }> }) => Promise<ServerWebSocketHandler>,
    // set later in listening
    upgradeStrategy?: (exchange: ServerWebExchange) => void,
    close?: () => Promise<void>
}
